import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  alpha,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Alert,
  Container,
  Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RefreshIcon from '@mui/icons-material/Refresh';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import SpeedIcon from '@mui/icons-material/Speed';
import { useNavigate } from 'react-router-dom';
import { useEnhancedGoogleDrive } from '../../hooks/useEnhancedGoogleDrive';
import VirtualizedHandbookList from './VirtualizedHandbookList';
import HandbookPreview from './HandbookPreview';

const FAVORITES_KEY = 'handbook_favorites';
const RECENT_KEY = 'handbook_recent';
const MAX_RECENT = 10;

/**
 * HandbookLibrary with improved performance, security, and caching
 * Features:
 * - Secure Google Drive authentication
 * - Intelligent caching for faster loading
 * - Virtual scrolling for large datasets
 * - Performance monitoring and analytics
 * - Network status indicators
 * - Enhanced error handling and recovery
 */
const EnhancedHandbookLibrary = () => {
  const navigate = useNavigate();
  
  // Enhanced Google Drive integration
  const {
    handbooks,
    loading,
    error,
    refreshHandbooks,
    analytics,
    getPerformanceMetrics,
    TokenExpirationDialog,
    isOnline,
    hasData,
    cacheHitRate,
    errorRate
  } = useEnhancedGoogleDrive({
    autoRefresh: true,
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    enableAnalytics: true
  });

  // Local state
  const [selectedHandbook, setSelectedHandbook] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);

  // Handle fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) {
      setEditMode(false); // Reset edit mode when exiting fullscreen
    }
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  // Handle edit mode toggle
  const toggleEditMode = useCallback(() => {
    setEditMode(!editMode);
  }, [editMode]);

  // Clean handbook name by removing "- SOS Handbook" suffix
  const cleanHandbookName = useCallback((name) => {
    return name.replace(/ - SOS Handbook$/i, '').replace(/-SOS Handbook$/i, '').trim();
  }, []);

  // Favorites and recent handbooks (localStorage)
  const [favorites, setFavorites] = useState(() => {
    try {
      const saved = localStorage.getItem(FAVORITES_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading favorites:', error);
      return [];
    }
  });

  const [recentHandbooks, setRecentHandbooks] = useState(() => {
    try {
      const saved = localStorage.getItem(RECENT_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading recent handbooks:', error);
      return [];
    }
  });

  // No need for filteredHandbooks - VirtualizedHandbookList handles filtering internally

  // Handle handbook selection
  const handleHandbookSelect = useCallback((handbook) => {
    const newSelection = selectedHandbook?.id === handbook?.id ? null : handbook;
    setSelectedHandbook(newSelection);
    
    if (newSelection) {
      // Update recent handbooks
      setRecentHandbooks(prev => {
        const filtered = prev.filter(id => id !== newSelection.id);
        const newRecent = [newSelection.id, ...filtered].slice(0, MAX_RECENT);
        localStorage.setItem(RECENT_KEY, JSON.stringify(newRecent));
        return newRecent;
      });
    }
  }, [selectedHandbook]);

  // Handle favorites toggle
  const toggleFavorite = useCallback((handbookId) => {
    setFavorites(prev => {
      const newFavorites = prev.includes(handbookId)
        ? prev.filter(id => id !== handbookId)
        : [...prev, handbookId];
      
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(newFavorites));
      return newFavorites;
    });
  }, []);


  // Handle manual refresh
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refreshHandbooks();
    } catch (error) {
      console.error("Failed to refresh handbooks:", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshHandbooks]);

  // Update performance metrics periodically
  useEffect(() => {
    const updateMetrics = () => {
      const metrics = getPerformanceMetrics();
      setPerformanceMetrics(metrics);
    };

    updateMetrics();
    const interval = setInterval(updateMetrics, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [getPerformanceMetrics]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle shortcuts when in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      if (e.key === 'Escape' && isFullscreen) {
        e.preventDefault();
        setIsFullscreen(false);
      } else if (e.key.toLowerCase() === 'f' && !e.ctrlKey && selectedHandbook) {
        e.preventDefault();
        setIsFullscreen(!isFullscreen);
      } else if (e.key.toLowerCase() === 'e' && !e.ctrlKey && selectedHandbook && isFullscreen) {
        e.preventDefault();
        setEditMode(!editMode);
      } else if (e.key === ' ' && selectedHandbook && !isFullscreen) {
        e.preventDefault();
        toggleFavorite(selectedHandbook.id);
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [selectedHandbook, isFullscreen, editMode, toggleFavorite]);

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        px: { xs: 0, sm: 2 },
        mb: { xs: 1, sm: 1 }
      }}
    >
      <Box sx={{ 
        maxWidth: '1400px',
        margin: '0 auto',
        p: { xs: 2, md: 4 },

        height: 'calc(100vh - 149px)', // Updated for header height
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden' // Prevent page scroll
      }}>
        {/* Header */}
        <Box sx={{ 
          mb: 4,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={() => navigate('/app')} 
              sx={{
                color: 'text.secondary',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
                },
                borderRadius: 2
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ 
                fontFamily: 'StrawberrySansDisplay',
                fontWeight: 400,
                mb: 1,
                color: 'text.primary',
                fontSize: { xs: '1.5rem', md: '2rem' },
                letterSpacing: '-0.02em'
              }}>
                Handbook Library
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ 
                  opacity: 0.8,
                  fontFamily: 'StrawberrySansDisplay'
                }}
              >
                Access and view SOS handbooks from Google Drive
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 1.5,
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {/* Handbook Count Badge */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.75,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              border: '1px solid',
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.15)
            }}>
              <MenuBookIcon sx={{ 
                fontSize: 16, 
                color: 'primary.main'
              }} />
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'StrawberrySansText',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  color: 'primary.main'
                }}
              >
                {handbooks.length} {handbooks.length === 1 ? 'Handbook' : 'Handbooks'}
              </Typography>
            </Box>

            {/* Status Indicator - Combined Network & Cache */}
            {isOnline && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.success.main, 0.15)
              }}>
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  bgcolor: 'success.main',
                  boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.success.main, 0.2)}`
                }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'StrawberrySansText',
                    fontWeight: 500,
                    fontSize: '0.7rem',
                    color: 'success.main'
                  }}
                >
                  {cacheHitRate > 0.5 ? 'Connected' : 'Online'}
                </Typography>
              </Box>
            )}

            {/* Action Buttons Group */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Refresh handbooks">
                <IconButton 
                  onClick={handleRefresh}
                  disabled={isRefreshing || loading}
                  size="small"
                  sx={{
                    color: isRefreshing ? 'primary.main' : 'text.secondary',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    '&:hover': {
                      color: 'primary.main',
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
                    },
                    borderRadius: 1.5,
                    animation: isRefreshing ? 'spin 1.5s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message || 'An error occurred while loading handbooks'}
          </Alert>
        )}

        {/* Main Content - Fixed Height */}
        <Paper
          elevation={0}
          sx={{ 
            height: 'calc(100vh - 200px)', // Fixed height accounting for header
            display: 'flex',
            gap: 0,
            overflow: 'hidden',
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.2s ease-in-out',
            boxShadow: (theme) => `0 4px 24px ${alpha(theme.palette.common.black, 0.03)}`,
            '&:hover': {
              boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.common.black, 0.06)}`,
            }
          }}
        >
          {/* Handbook List - Fixed Width with Scrolling */}
          <Box sx={{ 
            width: 320,
            height: '100%',
            borderRight: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            display: isFullscreen ? 'none' : 'flex',
            flexDirection: 'column'
          }}>
            <VirtualizedHandbookList
              handbooks={handbooks}
              selectedHandbook={selectedHandbook}
              onSelect={handleHandbookSelect}
              loading={loading}
              error={error}
              favorites={favorites}
              onToggleFavorite={toggleFavorite}
              recentHandbooks={recentHandbooks}
            />
          </Box>

          {/* Preview - Fixed Height with Scrolling */}
          <Box sx={{ 
            flex: 1,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative'
          }}>
          {selectedHandbook ? (
            <>
              <Box sx={{ 
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 1
              }}>
                <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Open in Fullscreen (F)"}>
                  <IconButton
                    onClick={toggleFullscreen}
                    sx={{
                      bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
                      backdropFilter: 'blur(8px)',
                      boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
                      border: '1px solid',
                      borderColor: (theme) => alpha(theme.palette.divider, 0.2),
                      color: 'text.secondary',
                      '&:hover': {
                        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
                        color: 'primary.main',
                        transform: 'translateY(-1px)',
                        boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ height: '100%', overflow: 'hidden' }}>
                <HandbookPreview handbook={selectedHandbook} />
              </Box>
            </>
            ) : (
              <Box sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: { xs: 3, sm: 4, md: 5 },
                px: { xs: 3, sm: 4, md: 5 },
                textAlign: 'center',
                background: 'transparent'
              }}>
                {/* Welcome Section */}
                <Stack 
                  spacing={0}
                  alignItems="center" 
                  sx={{ 
                    maxWidth: 480,
                    width: '100%',
                    textAlign: 'center'
                  }}
                >
                  {/* Hero Icon */}
                  <Box sx={{
                    width: { xs: 64, sm: 72, md: 80 },
                    height: { xs: 64, sm: 72, md: 80 },
                    borderRadius: 3,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    mb: 3,
                    border: '1px solid',
                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.2)
                  }}>
                    <MenuBookIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 } }} />
                  </Box>

                  {/* Title */}
                  <Typography variant="h4" sx={{ 
                    mb: 2,
                    fontFamily: 'StrawberrySansDisplay',
                    fontWeight: 500,
                    color: 'text.primary',
                    letterSpacing: '-0.02em',
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
                  }}>
                    Handbook Library
                  </Typography>

                  {/* Description */}
                  <Typography variant="body1" sx={{ 
                    mb: 4,
                    maxWidth: 480,
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    fontFamily: 'StrawberrySansText'
                  }}>
                    Select a handbook from the list to access SOS documentation. Search by name or browse by category.
                  </Typography>

                  {/* Quick Stats - Simplified */}
                  {hasData && handbooks.length > 0 && (
                    <Box sx={{ 
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                      gap: 2,
                      width: '100%',
                      maxWidth: 480,
                      mb: 4
                    }}>
                      {/* Handbooks Count */}
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          bgcolor: (theme) => alpha(theme.palette.background.default, 0.6),
                          border: '1px solid',
                          borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                          textAlign: 'center',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                            bgcolor: (theme) => alpha(theme.palette.background.default, 0.8),
                          }
                        }}
                      >
                        <MenuBookIcon sx={{ 
                          fontSize: 28, 
                          color: 'primary.main',
                          mb: 1,
                          opacity: 0.8
                        }} />
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontWeight: 600, 
                            fontFamily: 'StrawberrySansDisplay',
                            fontSize: { xs: '1.75rem', sm: '2rem' },
                            color: 'primary.main',
                            mb: 0.5
                          }}
                        >
                          {handbooks.length}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        >
                          Handbooks Available
                        </Typography>
                      </Paper>

                      {/* Cache Performance - Only show if significant */}
                      {cacheHitRate > 0.3 && (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            bgcolor: (theme) => alpha(theme.palette.background.default, 0.6),
                            border: '1px solid',
                            borderColor: (theme) => alpha(
                              cacheHitRate > 0.7 ? theme.palette.success.main : theme.palette.divider, 
                              0.2
                            ),
                            textAlign: 'center',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: (theme) => alpha(
                                cacheHitRate > 0.7 ? theme.palette.success.main : theme.palette.warning.main, 
                                0.3
                              ),
                              bgcolor: (theme) => alpha(theme.palette.background.default, 0.8),
                            }
                          }}
                        >
                          <SpeedIcon sx={{ 
                            fontSize: 28, 
                            color: cacheHitRate > 0.7 ? 'success.main' : 'warning.main',
                            mb: 1,
                            opacity: 0.8
                          }} />
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              fontWeight: 600, 
                              fontFamily: 'StrawberrySansDisplay',
                              fontSize: { xs: '1.75rem', sm: '2rem' },
                              color: cacheHitRate > 0.7 ? 'success.main' : 'warning.main',
                              mb: 0.5
                            }}
                          >
                            {(cacheHitRate * 100).toFixed(0)}%
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary',
                              fontSize: '0.75rem',
                              fontWeight: 500
                            }}
                          >
                            Cache Performance
                          </Typography>
                        </Paper>
                      )}
                    </Box>
                  )}
                </Stack>
              </Box>
            )}
          </Box>
        </Paper>


        {/* Enhanced Fullscreen Dialog */}
        <Dialog
          fullScreen
          open={isFullscreen}
          onClose={() => setIsFullscreen(false)}
          TransitionProps={{
            timeout: 400
          }}
          PaperProps={{
            sx: {
              bgcolor: 'background.default',
              backgroundImage: 'none'
            }
          }}
        >
          <DialogContent sx={{ p: 0, position: 'relative', height: '100vh', overflow: 'hidden' }}>
            {/* Redesigned Fullscreen Header */}
            {selectedHandbook && (
              <Box sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 100,
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.92),
                backdropFilter: 'blur(20px)',
                borderBottom: '1px solid',
                borderColor: (theme) => alpha(theme.palette.divider, 0.08),
                px: 4,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.3s ease',
                boxShadow: (theme) => `0 1px 20px ${alpha(theme.palette.common.black, 0.02)}`
              }}>
                
                {/* Left Section - Handbook Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1, minWidth: 0 }}>
                  <Box sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    flexShrink: 0
                  }}>
                    <MenuBookIcon sx={{ fontSize: 20 }} />
                  </Box>
                  
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="h6" sx={{
                      fontFamily: 'StrawberrySansDisplay',
                      fontWeight: 500,
                      color: 'text.primary',
                      fontSize: '1rem',
                      lineHeight: 1.2,
                      mb: 0.25,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {cleanHandbookName(selectedHandbook.name)}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        px: 1.5,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor: editMode 
                          ? (theme) => alpha(theme.palette.primary.main, 0.12)
                          : (theme) => alpha(theme.palette.text.secondary, 0.06),
                        border: '1px solid',
                        borderColor: editMode 
                          ? (theme) => alpha(theme.palette.primary.main, 0.2)
                          : (theme) => alpha(theme.palette.divider, 0.2)
                      }}>
                        <Typography variant="caption" sx={{
                          fontSize: '0.7rem',
                          fontFamily: 'StrawberrySansText',
                          fontWeight: 600,
                          color: editMode ? 'primary.main' : 'text.secondary',
                          letterSpacing: '0.02em',
                          textTransform: 'uppercase',
                          lineHeight: 1
                        }}>
                          {editMode ? 'Edit Mode' : 'View Mode'}
                        </Typography>
                      </Box>
                      
                      {handbooks.length > 1 && (
                        <Typography variant="caption" sx={{
                          color: 'text.secondary',
                          fontFamily: 'StrawberrySansText',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          letterSpacing: '0.01em'
                        }}>
                          {handbooks.findIndex(h => h.id === selectedHandbook?.id) + 1} of {handbooks.length}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Right Section - Controls */}
                <Box sx={{ 
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  flexShrink: 0
                }}>
                  
                  {/* Navigation Controls */}
                  {handbooks.length > 1 && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.5,
                      mr: 1,
                      p: 0.5,
                      borderRadius: 1.5,
                      bgcolor: (theme) => alpha(theme.palette.action.hover, 0.04),
                      border: '1px solid',
                      borderColor: (theme) => alpha(theme.palette.divider, 0.08)
                    }}>
                      <Tooltip title="Previous (←)" arrow placement="bottom">
                        <span>
                          <IconButton
                            onClick={() => {
                              const currentIndex = handbooks.findIndex(h => h.id === selectedHandbook?.id);
                              if (currentIndex > 0) {
                                handleHandbookSelect(handbooks[currentIndex - 1]);
                              }
                            }}
                            disabled={handbooks.findIndex(h => h.id === selectedHandbook?.id) === 0}
                            size="small"
                            sx={{
                              width: 32,
                              height: 32,
                              color: 'text.secondary',
                              '&:hover': {
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                color: 'primary.main',
                                transform: 'translateX(-1px)'
                              },
                              '&.Mui-disabled': {
                                opacity: 0.3
                              },
                              transition: 'all 0.15s ease'
                            }}
                          >
                            <NavigateBeforeIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      
                      <Tooltip title="Next (→)" arrow placement="bottom">
                        <span>
                          <IconButton
                            onClick={() => {
                              const currentIndex = handbooks.findIndex(h => h.id === selectedHandbook?.id);
                              if (currentIndex < handbooks.length - 1) {
                                handleHandbookSelect(handbooks[currentIndex + 1]);
                              }
                            }}
                            disabled={handbooks.findIndex(h => h.id === selectedHandbook?.id) === handbooks.length - 1}
                            size="small"
                            sx={{
                              width: 32,
                              height: 32,
                              color: 'text.secondary',
                              '&:hover': {
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                color: 'primary.main',
                                transform: 'translateX(1px)'
                              },
                              '&.Mui-disabled': {
                                opacity: 0.3
                              },
                              transition: 'all 0.15s ease'
                            }}
                          >
                            <Box sx={{ transform: 'rotate(180deg)' }}>
                              <NavigateBeforeIcon sx={{ fontSize: 18 }} />
                            </Box>
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  )}

                  {/* Mode Toggle */}
                  <Tooltip title={`Switch to ${editMode ? 'view' : 'edit'} mode (E)`} arrow placement="bottom">
                    <Button
                      variant={editMode ? 'contained' : 'outlined'}
                      onClick={toggleEditMode}
                      startIcon={editMode ? <VisibilityIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        height: 36,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontFamily: 'StrawberrySansText',
                        fontWeight: 500,
                        px: 2,
                        fontSize: '0.8rem',
                        minWidth: 'auto',
                        boxShadow: 'none',
                        bgcolor: editMode ? 'primary.main' : 'transparent',
                        color: editMode ? 'primary.contrastText' : 'text.primary',
                        border: '1px solid',
                        borderColor: editMode ? 'primary.main' : (theme) => alpha(theme.palette.divider, 0.2),
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          transform: 'translateY(-0.5px)',
                          boxShadow: editMode ? 2 : 1,
                          bgcolor: editMode ? 'primary.dark' : (theme) => alpha(theme.palette.primary.main, 0.04),
                          borderColor: editMode ? 'primary.dark' : 'primary.main'
                        }
                      }}
                    >
                      {editMode ? 'View' : 'Edit'}
                    </Button>
                  </Tooltip>
                  
                  {/* Exit Button */}
                  <Tooltip title="Exit fullscreen (Esc)" arrow placement="bottom">
                    <IconButton
                      onClick={() => setIsFullscreen(false)}
                      sx={{
                        width: 36,
                        height: 36,
                        color: 'text.secondary',
                        bgcolor: (theme) => alpha(theme.palette.action.hover, 0.04),
                        border: '1px solid',
                        borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                        borderRadius: 1.5,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          color: 'error.main',
                          bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                          borderColor: (theme) => alpha(theme.palette.error.main, 0.2),
                          transform: 'translateY(-0.5px)',
                          boxShadow: 1
                        }
                      }}
                    >
                      <FullscreenExitIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
            
            {/* Handbook Content */}
            <Box sx={{ 
              pt: selectedHandbook ? '64px' : 0, // Account for compact header bar
              height: '100vh',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {selectedHandbook && <HandbookPreview handbook={selectedHandbook} editMode={editMode} />}
            </Box>
          </DialogContent>
        </Dialog>

        {/* Analytics Dialog */}
        <Dialog
          open={showAnalytics}
          onClose={() => setShowAnalytics(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider'
            }
          }}
        >
          <Box sx={{ 
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main'
            }}>
              <AnalyticsIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{
                fontFamily: 'StrawberrySansDisplay',
                fontWeight: 500
              }}>
                Performance Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Caching stores data locally to reduce Google Drive API calls and improve loading speed.
              </Typography>
            </Box>
          </Box>
          <DialogContent sx={{ pt: 3 }}>
          
          {performanceMetrics && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" paragraph>
                <strong>Network:</strong> {performanceMetrics.networkStatus}
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Handbooks Loaded:</strong> {performanceMetrics.handbookCount}
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Cache Hit Rate:</strong> {(cacheHitRate * 100).toFixed(1)}% 
                <Typography component="span" variant="caption" color="text.secondary">
                  (% of requests served from local storage)
                </Typography>
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Error Rate:</strong> {(errorRate * 100).toFixed(1)}%
                <Typography component="span" variant="caption" color="text.secondary">
                  (% of failed requests)
                </Typography>
              </Typography>
              {analytics && (
                <>
                  <Typography variant="body2" paragraph>
                    <strong>API Requests:</strong> {analytics.requests}
                    <Typography component="span" variant="caption" color="text.secondary">
                      (total calls to Google Drive)
                    </Typography>
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Cache Hits:</strong> {analytics.cacheHits}
                    <Typography component="span" variant="caption" color="text.secondary">
                      (times data loaded from local storage)
                    </Typography>
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Cache Misses:</strong> {analytics.cacheMisses}
                    <Typography component="span" variant="caption" color="text.secondary">
                      (times data fetched from Google Drive)
                    </Typography>
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Average Response Time:</strong> {analytics.averageResponseTime?.toFixed(0)}ms
                  </Typography>
                </>
              )}
            </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button 
              onClick={() => setShowAnalytics(false)}
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none'
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Token Expiration Dialog */}
        {TokenExpirationDialog}
      </Box>
    </Container>
  );
};

export default EnhancedHandbookLibrary;